/**
 *
 * @export
 * @class BaseAPI
 */

import * as portableFetch from "portable-fetch";
import {Configuration} from "./configuration";
import {FetchAPI} from "./fetchApi";
import {setProdModeModalVisible} from "../../redux/prodmodemodal/prodModeModalActions";
import {keycloak} from "../../keycloak-config";

class Profile {
    givenName?: string;
    sn?: string;
    telephoneNumber?: string;
    userPassword?: string;
    currentPassword?: string;
}

// class UpdateProfile {
//     firstName?: string;
//     lastName?: string;
//     company?: string;
//     phone?: string;
// }

const BASE_PATH = process.env.REACT_APP_PORTAL_BASE_PATH || "http://localhost:8080";

export class BaseAPI {
    protected configuration: Configuration;

    constructor(configuration?: Configuration,
                protected basePath: string = BASE_PATH,
                protected fetch: FetchAPI = portableFetch,
    ) {
        if (configuration) {
            this.configuration = configuration;
            this.basePath = configuration.basePath || this.basePath;
        }
    }
}

class ResultDto<D> {
    data: D;
    message: string;
    warning: string;
}

class TermAgreementInfo {
    id: number;
    agreementDate: string;
    version: number;
}

export class ApiInformation {
    id: string;
    path: string;
    name: string;
    headerName: string;
    order: number;
    type: ApiType;
    informationURL: string;
    informationHeader: string;
    information: Array<string>;
    scopeDescription: ScopeDescription;
    developerHint: string;
    desc: string;
    background: string;
    statusPageId: string;
    availability: string;
    postmanCollectionId: string;
    enabledPostmanButton: boolean;
    hasPermission: boolean;
}

class ScopeDescription {
    default: string;
    mainPage: string;
}

enum ApiType {
    B2B = "B2B",
    B2C = "B2C",
}

class OtpResponse {
    sessionId: string;
    ordinalNumber: number;
    secsToNextTry: number;
}

export class EventType {
    id?: string;
    name?: string;
    type?: string;
    used?: boolean;
    maximumAttemptsCount?: string;
    priority?: string;
    description?: string;
}

interface TypedResponse<T = any> extends Response {
    /**
     * this will override `json` method from `Body` that is extended by `Response`
     * interface Body {
     *     json(): Promise<any>;
     * }
     */
    json<P = T>(): Promise<P>
}

    export function isProdModeAvailable(status, response) {
    if (status === 403 && response === ErrorStatus.PROD_MODE_SESSION_OUTDATED) {
        setProdModeModalVisible(true);
        return false;
    }
    return true;
}

function withDefaultHeaders(props: any) {
    if (keycloak.authenticated) {
        const nextProps = {...props, "Authorization": "Bearer " + keycloak.token};
        return nextProps;
    }
    return props;
}

export class PortalApi extends BaseAPI {
    // public async sendSupportRequest(request: SupportRequest): Promise<TypedResponse<ResultDto<Object>>> {
    //     const {basePath} = this;
    //     const url = `${basePath}/support/submit`;

    //     return fetch(url, {
    //         headers: withDefaultHeaders({
    //             "Content-Type": "application/json",
    //             "accept": "application/json",
    //             "X-Requested-With": " XMLHttpRequest",
    //         }),
    //         credentials: 'include',
    //         body: JSON.stringify(request),
    //         method: 'POST',
    //     });
    // }

    downloadCertificate(certificateId: number) {
        const {basePath} = this;
        const url = `${basePath}/certificate/download/${certificateId}`;

        return fetch(url, {
            headers: withDefaultHeaders({
            }),
            credentials: 'include',
            method: 'GET',
        });
    }

    public async getUser() {
        const {basePath} = this;
        const registerUrl = `${basePath}/proxy/user/get`;

        return fetch(registerUrl, {
            headers: withDefaultHeaders({
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest",
                "Accept-API-Version": "protocol=1.0,resource=2.0"
            }),
            credentials: 'include',
            method: 'GET',
        });
    }

    public async updateProfileWithOtpStart(phone: string): Promise<TypedResponse<OtpResponse>> {
        const {basePath} = this;
        const getUrl = `${basePath}/proxy/user/otps?phoneNumber=${phone}`;
        return fetch(getUrl, {
            headers: withDefaultHeaders({
                "Accept": "application/json",
                "X-Requested-With": " XMLHttpRequest",
            }),
            credentials: 'include',
            method: 'POST',
        });
    }

    public async updateProfileWithOtp(sessionId: string, otp: string, request: Profile) {
        const {basePath} = this;
        const otpEscape = otp.replaceAll('%', '');
        const updateUrl = `${basePath}/proxy/user/put?sessionId=${sessionId}&otp=${otpEscape}`;
        return fetch(updateUrl, {
            headers: withDefaultHeaders({
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-Requested-With": " XMLHttpRequest",
            }),
            credentials: 'include',
            body: JSON.stringify(request),
            method: 'PUT',
        });
    }

    public async updateProfileResendOtp(phone: string, sessionId: string): Promise<TypedResponse<OtpResponse>> {
        const {basePath} = this;
        const getUrl = `${basePath}/proxy/user/otps?phoneNumber=${phone}&sessionId=${sessionId}`;
        return fetch(getUrl, {
            headers: withDefaultHeaders({
                "Accept": "application/json",
                "X-Requested-With": " XMLHttpRequest",
            }),
            credentials: 'include',
            method: 'POST',
        });
    }

    public async checkTermAgreement(): Promise<TypedResponse<ResultDto<boolean>>> {
        const {basePath} = this;
        const url = `${basePath}/termagreement/check`;

        return fetch(url, {
            headers: withDefaultHeaders({
            }),
            credentials: 'include',
            method: 'GET',
        });
    }

    public async saveTermAgreement(): Promise<TypedResponse<ResultDto<TermAgreementInfo>>> {
        const {basePath} = this;
        const url = `${basePath}/termagreement/save`;

        return fetch(url, {
            headers: withDefaultHeaders({
            }),
            credentials: 'include',
            method: 'POST',
        });
    }

    /**
     * ForgerockAmApi - functional programming interface
     * @export
     */

    public async getSessionInfo() {
        const {basePath} = this;
        const sessionUrl = `${basePath}/proxy/sessions?_action=getSessionInfo`;
        return fetch(sessionUrl, {
            "headers": withDefaultHeaders({
                "accept": "application/json",
                "content-type": "application/json",
                "sec-fetch-dest": "empty",
                "sec-fetch-mode": "cors",
                "sec-fetch-site": "same-site",
                "x-requested-with": "XMLHttpRequest"
            }),
            "method": "POST",
            "mode": "cors",
            "credentials": "include"
        });
    }

    public async logout(): Promise<any> {
        const {basePath} = this;
        const authenticateUrl = `${basePath}/proxy/sessions?_action=logout`;

        return fetch(authenticateUrl, {
            headers: withDefaultHeaders({
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest"
            }),
            credentials: 'include',
            method: 'POST',
        });
    }

    public async getApiList(): Promise<TypedResponse<ResultDto<Array<ApiInformation>>>> {
        const {basePath} = this;
        const url = `${basePath}/scope/information`;

        return fetch(url, {
            headers: withDefaultHeaders({
                "Content-Type": "application/json",
                "accept": "application/json"
            }),
            credentials: 'omit',
            method: 'GET',
        });
    }

    public async getApiById(id: string): Promise<TypedResponse<ResultDto<ApiInformation>>>  {
        const {basePath} = this;
        const url = `${basePath}/scope/information/${id}`;

        return fetch(url, {
            headers: withDefaultHeaders({
                "Content-Type": "application/json",
                "accept": "application/json"
            }),
            credentials: 'include',
            method: 'GET',
        });
    }

    downloadCACertificate() {
        const {basePath} = this;
        const url = `${basePath}/certificate/download/cacert`;

        return fetch(url, {
            headers: {
            },
            credentials: 'include',
            method: 'GET',
        });
    }

    downloadPostmanCollection(apiId: string) {
        const {basePath} = this;
        const url = `${basePath}/api/collection/${apiId}/postman`;

        return fetch(url, {
            headers: {
            },
            credentials: 'include',
            method: 'GET',
        });
    }

    downloadWebhookCertificate(appId: string, webhookId: string, certificateId: string) {
        const {basePath} = this;
        const url = `${basePath}/webhooks/${appId}/${webhookId}/certificates/${certificateId}`;

        return fetch(url, {
            headers: withDefaultHeaders({
            }),
            credentials: 'include',
            method: 'GET',
        });
    }

    downloadWebhookCACertificate() {
        const {basePath} = this;
        const url = `${basePath}/webhooks/cacert`;

        return fetch(url, {
            headers: {
            },
            credentials: 'include',
            method: 'GET',
        });
    }

    public async testWebhook(appId: string, webhookId: string) {
        const {basePath} = this;
        const url = `${basePath}/webhooks/${appId}/${webhookId}/test`;

        return fetch(url, {
            headers: withDefaultHeaders({
            }),
            credentials: 'include',
            method: 'POST',
        });
    }

    // public async is2FAEnabled(): Promise<TypedResponse<boolean>> {
    //     const {basePath} = this;
    //     const url = `${basePath}/user/2fa`;

    //     return fetch(url, {
    //         headers: withDefaultHeaders({
    //             "Content-Type": "application/json",
    //             "accept": "application/json",
    //         }),
    //         credentials: 'include',
    //         method: 'GET',
    //     });
    // }

    // public async disable2FA(): Promise<TypedResponse<boolean>> {
    //     const {basePath} = this;
    //     const url = `${basePath}/user/2fa`;

    //     return fetch(url, {
    //         headers: withDefaultHeaders({
    //             "Content-Type": "application/json",
    //             "accept": "application/json",
    //         }),
    //         credentials: 'include',
    //         method: 'DELETE',
    //     });
    // }
}


export class UserConfirmedRegistrationInfo {
    input: UserRegistrationInfo;
    token: string
}


class UserOtpInfo {
    otp_code: string;
}


export class UserConfirmOtpInfo {
    input: UserOtpInfo;
    token: string
}

class UserRegistrationInfo {
    username: string;
    givenName: string;
    sn: string;
    userPassword: string;
    mail?: string;
    company?: string;
    cif?: string;
    telephoneNumber?: string;
}

interface TypedResponse<T = any> extends Response {
    /**
     * this will override `json` method from `Body` that is extended by `Response`
     * interface Body {
     *     json(): Promise<any>;
     * }
     */
    json<P = T>(): Promise<P>
}

// class SupportRequest {
//     firstName? : string;
//     lastName? : string;
//     requestText? : string;
// }

export const ErrorStatus = Object.freeze({
    SESSION_OUTDATED: 'SESSION_OUTDATED',
    NO_ACCESS: 'NO_ACCESS',
    NO_AUTH: 'NO_AUTH',
    UNKNOWN: 'UNKNOWN',
    PROD_MODE_SESSION_OUTDATED: 'PROD_MODE_SESSION_OUTDATED',
})
